import type { FC, ReactNode } from 'react';
import { Column, Grid, Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';

import styles from './PaginaDeModulo.module.scss';

// A PÁGINA DE UM MÓDULO DO PERFIL (Início, Músicas, Equipe, Arquivos…). Não tem cabeçalho de
// página: o nome do módulo já está aceso no SideNav. Com abas, as `Tabs` de linha do Carbon são o
// topo, com as ações da tela à direita na mesma barra; sem abas, a tela abre no conteúdo. O que vale
// para todas as abas (a cota de Arquivos, o painel do CRM) fica em `acima`, antes delas.
//
// Sem cabeçalho visível, mas com o `h1`: o nome do módulo vai escondido para o leitor de tela
// (`cds--visually-hidden`), senão a página não teria título nenhum para quem não vê.

export interface AbaDoModulo {
  chave: string;
  rotulo: string;
  /** A aba que ainda não existe (as quatro do Marketing): aparece, mas não abre. */
  desabilitada?: boolean;
  /** O que acompanha o rótulo ("em breve"), em texto de apoio. */
  sufixo?: string;
}

export const PaginaDeModulo: FC<{
  /** O nome do módulo: o `h1` da página, só para o leitor de tela. */
  titulo: string;
  /** As abas do módulo; o conteúdo da ativa é `children`. */
  abas?: { rotulo: string; itens: AbaDoModulo[]; ativa: string; aoMudar: (chave: string) => void };
  /** Ações da tela (criar, filtros), à direita das abas. */
  acoes?: ReactNode;
  /** O que descreve o módulo inteiro, acima das abas. */
  acima?: ReactNode;
  children: ReactNode;
}> = ({ titulo, abas, acoes, acima, children }) => {
  const h1 = <h1 className='cds--visually-hidden'>{titulo}</h1>;
  if (!abas) {
    return (
      <Grid className={styles.pagina}>
        <Column sm={4} md={8} lg={16} className={styles.coluna}>
          {h1}
          {acima}
          {acoes && <div className={styles.acoesSemAbas}>{acoes}</div>}
          {children}
        </Column>
      </Grid>
    );
  }

  const indice = Math.max(0, abas.itens.findIndex((a) => a.chave === abas.ativa));

  return (
    <Grid className={styles.pagina}>
      <Column sm={4} md={8} lg={16} className={styles.coluna}>
        {h1}
        {acima && <div className={styles.acima}>{acima}</div>}
        <Tabs selectedIndex={indice} onChange={({ selectedIndex }) => abas.aoMudar(abas.itens[selectedIndex].chave)}>
          <div className={styles.barra}>
            <TabList aria-label={abas.rotulo} className={styles.abas}>
              {abas.itens.map((a) => (
                <Tab key={a.chave} disabled={a.desabilitada}>
                  {a.rotulo}
                  {a.sufixo && <span className={styles.sufixo}>{a.sufixo}</span>}
                </Tab>
              ))}
            </TabList>
            {acoes && <div className={styles.acoes}>{acoes}</div>}
          </div>
          <TabPanels>
            {abas.itens.map((a, i) => (
              <TabPanel key={a.chave} className={styles.painel}>{i === indice ? children : null}</TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Column>
    </Grid>
  );
};
